import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ExternalLink, RefreshCw, Star } from "lucide-react";

const href = (u) => (/^https?:\/\//i.test(u) ? u : `https://${u}`);

export const ReviewLinksSection = ({ propertyId, isAdmin, canEdit, reminderOn, onReminderChange }) => {
  const [meta, setMeta] = useState(null);
  const [draft, setDraft] = useState({});
  const load = () => api.get(`/properties/${propertyId}/reviews-meta`).then((r) => { setMeta(r.data); setDraft(r.data.links || {}); }).catch(() => setMeta(null));
  useEffect(() => { if (propertyId) load(); }, [propertyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async (source, approved = true) => {
    try {
      await api.post(`/properties/${propertyId}/review-links/approve`, { source, url: draft[source] || "", approved });
      toast.success(approved ? `${source} link approved` : `${source} link saved`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const resuggest = async () => {
    await api.post(`/properties/${propertyId}/review-links/suggest`);
    toast.success("Links re-created from name & address (approved links kept)");
    load();
  };
  if (!meta) return null;
  return (
    <div className="space-y-3 rounded-lg border border-border p-4 md:col-span-2" data-testid="review-links-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <Label className="flex items-center gap-1.5 text-sm font-bold"><Star className="h-4 w-4 text-primary" /> Review Site Links</Label>
          <p className="text-xs text-muted-foreground">Created automatically from the hotel name &amp; address. Open each one — if it lands on this hotel, Approve; otherwise paste the hotel's exact review page and Approve.</p>
        </div>
        {isAdmin && <Button size="sm" variant="outline" onClick={resuggest} data-testid="review-links-resuggest"><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Re-create from address</Button>}
      </div>
      <div className="space-y-2">
        {meta.sources.map((s) => {
          const st = meta.status?.[s];
          const changed = (draft[s] || "") !== (meta.links?.[s] || "");
          return (
            <div key={s} className="grid items-center gap-2 md:grid-cols-[130px_1fr_auto]" data-testid={`review-link-row-${s}`}>
              <div className="text-sm font-semibold">{s}</div>
              <Input className="h-8 text-xs" value={draft[s] || ""} disabled={!isAdmin} placeholder={s === "Brand Website" ? "Paste brand page (optional)" : "Paste exact review page"}
                onChange={(e) => setDraft((d) => ({ ...d, [s]: e.target.value }))} data-testid={`review-link-input-${s}`} />
              <div className="flex items-center gap-1.5">
                {draft[s] && <Button size="sm" variant="ghost" className="h-8" onClick={() => window.open(href(draft[s]), "_blank", "noopener")} data-testid={`review-link-open-${s}`}><ExternalLink className="h-3.5 w-3.5" /></Button>}
                {st === "approved" && !changed ? (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600" title={meta.approved_by?.[s] ? `Approved by ${meta.approved_by[s]}` : ""} data-testid={`review-link-approved-${s}`}><CheckCircle2 className="h-4 w-4" /> Approved</span>
                ) : draft[s] ? (
                  isAdmin ? <Button size="sm" className="h-8" onClick={() => approve(s)} data-testid={`review-link-approve-${s}`}>Approve</Button>
                    : <span className="text-xs text-amber-600">Awaiting approval</span>
                ) : <span className="text-xs text-muted-foreground">—</span>}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between border-t border-border pt-3">
        <div>
          <Label className="text-sm font-semibold">Unreplied reviews email</Label>
          <p className="text-xs text-muted-foreground">Every day at <b>5 PM</b> (property time), email this property's GMs the reviews from the last 30 days still marked Not replied.</p>
        </div>
        <Switch checked={!!reminderOn} disabled={!canEdit} onCheckedChange={onReminderChange} data-testid="review-reply-reminder-enabled" />
      </div>
    </div>
  );
};
